<template>
  <v-row no-gutters class="fill-height">
    <v-col cols="12">
      <card-title-row class="pl-0 pt-5" :straight-title="additionalModuleName" :has-buttons="false" />
      <sidebar-row class="pt-5">
        <v-col cols="8" class="px-0 pb-7">
          <v-tooltip bottom max-width="300">
            <template #activator="{ on }">
              <span v-on="on">
                <data-input v-model="currentWaterDataProfileLayerId" v-bind="layerInput" />
              </span>
            </template>
            {{ layerTooltip }}
          </v-tooltip>
        </v-col>
      </sidebar-row>
      <sidebar-row>
        <v-tooltip bottom :disabled="waterDataProfileLayersAdded" max-width="300">
          <template #activator="{ on }">
            <span v-on="on">
              <base-button-subheader subheader-translation-path="sidebar.waterNetworkProfile.selectLinksSubtitle">
                <dots-menu
                  :items="dotsItems"
                  @single="turnOnSelecting('single')"
                  @multiple="turnOnSelecting('multiple')"
                  @freehand="turnOnSelecting('freehand')"
                >
                  <template #activator="{ on }">
                    <v-badge :content="selectedFeaturesLength" :value="isAnyFeatureSelected" color="error" overlap>
                      <base-button
                        v-on="getButtonListeners(on)"
                        @click="turnOffSelecting"
                        class="mr-0"
                        translation-path="sidebar.waterNetworkProfile.selectLinks"
                        :button-style="buttonStyle"
                        :disabled="!waterDataProfileLayersAdded"
                      >
                      </base-button>
                    </v-badge>
                  </template>
                </dots-menu>
              </base-button-subheader>
            </span>
          </template>
          {{ $i18n.t('snackbar.modulesNotConfigurate.waterNetworkProfile') }}
        </v-tooltip>
      </sidebar-row>
      <template v-if="isAnyFeatureSelected">
        <sidebar-row class="pt-5 pr-5">
          <v-row no-gutters class="justify-end">
            <v-col cols="auto">
              <clickable-text @click="deleteFeature(null)">
                {{ $i18n.t('sidebar.waterNetworkProfile.deleteAllLinks') }}
              </clickable-text>
            </v-col>
          </v-row>
          <ribbon-list
            :listItems="computedSelectedFeatures"
            styleType="raw"
            @mouseleave="onElementHover($event, false)"
            @mouseenter="onElementHover($event, true)"
          >
            <template #itemButtons-prepend="{ item }">
              <v-icon @click="deleteFeature(item.value)" color="rgba(0,0,0,.7)" size="19px">mdi-close</v-icon>
            </template>
          </ribbon-list>
        </sidebar-row>
        <sidebar-row class="pt-5" slot-cols="auto">
          <base-button
            class="mr-0"
            translation-path="sidebar.waterNetworkProfile.generateProfile"
            @click="onOpened"
            :loading="isLoading"
          >
          </base-button>
        </sidebar-row>
      </template>
      <data-profile-dialog
        v-if="isDialogLoaded"
        :is-visible.sync="isDialogVisible"
        :image="image"
        :image-name="imageName"
        title-path="sidebar.waterNetworkProfile.profileTitle"
      />
    </v-col>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import ClickableText from '@/components/ClickableText';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import RibbonList from '@/components/RibbonList';
import SidebarRow from '@/components/SidebarRow';

import identificationSidebarCard from '@/mixins/identificationSidebarCard';

export default {
  name: 'WaterDataProfile',
  components: {
    CardTitleRow,
    ClickableText,
    DataInput,
    DataProfileDialog: () => import('@/components/DataProfileDialog'),
    DotsMenu,
    RibbonList,
    SidebarRow,
  },
  mixins: [identificationSidebarCard],
  computed: {
    activeTool: get('tools/activeTool'),
    additionalModuleName: get('admin/additionalModules@WATER_NETWORK_PROFILE.verbose_name'),
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    waterDataProfileDataSources: get('admin/modulesMapping@water_network_profile.datasources'),
    waterDataProfileDataSourcesArray() {
      return this.waterDataProfileDataSources ? Object.values(this.waterDataProfileDataSources) : [];
    },
    buttonStyle() {
      return this.isButtonActive
        ? {
            backgroundColor: `${this.$_colors.primarybackground} !important`,
            border: '1px solid rgba(26, 115, 232, 0.3) !important',
            color: this.$_colors.primary,
          }
        : {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            border: '1px solid rgba(0, 0, 0, 0.2) !important',
            color: '#61646D',
          };
    },
    dotsItems() {
      return [
        {
          name: 'single',
        },
        {
          name: 'multiple',
          disabled: !this.mapPanelDataFetched,
        },
        {
          name: 'freehand',
          disabled: !this.mapPanelDataFetched,
        },
      ];
    },
    currentWaterDataProfileLayer() {
      return this.layers[this.currentWaterDataProfileLayerId] || undefined;
    },
    currentWaterDataProfileDataSourceName() {
      return this.currentWaterDataProfileLayer?.data_source_name || '';
    },
    currentWaterDataProfileDataSource() {
      return this.dataSources[this.currentWaterDataProfileDataSourceName] || undefined;
    },
    currentWaterDataProfileDataSourceGeomName() {
      return this.currentWaterDataProfileDataSource?.attributes_schema.geometry_name;
    },
    layerInput() {
      return {
        dataType: { name: 'select' },
        disabled: !this.waterDataProfileLayersAdded,
        hideDetails: true,
        items: this.waterDataProfileMappedLayers,
        label: this.$i18n.t('sidebar.waterNetworkProfile.waterDataProfileLayer'),
      };
    },
    layerTooltip() {
      return (
        this.currentWaterDataProfileLayer?.name || this.$i18n.t('snackbar.modulesNotConfigurate.waterNetworkProfile')
      );
    },
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    waterDataProfileLayers() {
      return this.projectLayers.filter(layer => this.waterDataProfileDataSourcesArray.includes(layer.data_source_name));
    },
    waterDataProfileLayersAdded() {
      return this.waterDataProfileLayers?.length > 0;
    },
    waterDataProfileMappedLayers() {
      return this.waterDataProfileLayers.map(layer => {
        return { text: layer.name, value: layer.id };
      });
    },

    selectedFeatures: get('map/selectedFeatures@:currentWaterDataProfileLayerId'),
    computedSelectedFeatures() {
      return (this.selectedFeatures || []).map(feature => {
        return { value: feature, title: `ID: ${feature}` };
      });
    },
    selectedFeaturesLength() {
      return this.computedSelectedFeatures.length;
    },
    isAnyFeatureSelected() {
      return this.selectedFeaturesLength > 0;
    },
  },
  data: () => ({
    currentWaterDataProfileLayerId: null,
    image: undefined,
    imageName: undefined,
    isButtonActive: false,
    isDialogLoaded: false,
    isDialogVisible: false,
    isLoading: false,
  }),
  methods: {
    getLayerFeatures: call('layers/getLayerFeatures'),
    getProfile: call('modeling/getWaterDataProfile'),
    async onOpened() {
      try {
        this.isLoading = true;
        const payload = {
          features: this.selectedFeatures,
          layer: this.currentWaterDataProfileLayerId,
        };
        const r = await this.getProfile(payload);
        this.imageName = this.$_extractFilename(r.headers);
        this.image = this.$_createImageFromArrayBuffer(r.data, r.headers);
        this.isDialogLoaded = true;
        this.isDialogVisible = true;
        this.isLoading = false;
      } catch {
        this.isLoading = false;
      }
    },
    async getIntersectingLinks(geometry) {
      const r = await this.getLayerFeatures({
        features_filter: {
          '%SPATIALLY_INTERSECTS': {
            [`${this.currentWaterDataProfileDataSourceName}.${this.currentWaterDataProfileDataSourceGeomName}`]: [
              geometry,
            ],
          },
        },
        layer_id: this.currentWaterDataProfileLayerId,
        params: {
          ids_only: true,
        },
      });
      return r;
    },
    clearSelection(layerId) {
      this.turnOffSelecting();
      if (layerId || layerId == '0') {
        this.deleteFeature(null, layerId);
      } else {
        this.deleteFeature(null);
      }
    },
    deleteFeature(id, layerId = this.currentWaterDataProfileLayerId) {
      const deletedIds = id ? [id] : [];
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: deletedIds,
        },
      });
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'highlitedFeatures',
        features: {
          [layerId]: deletedIds,
        },
      });
    },
    turnOffSelecting() {
      if (this.activeTool !== 'selection') {
        return;
      }
      this.$root.$emit('selection-action');
      this.isButtonActive = false;
    },
    turnOnSelecting(type) {
      if (type === 'single') {
        this.$root.$emit('attachOnClickSelection', this.currentWaterDataProfileLayerId, false);
      } else if (type === 'multiple') {
        this.$root.$emit('selection-action', 'multiple', {
          selectionBoxAction: this.getIntersectingLinks,
          layerId: this.currentWaterDataProfileLayerId,
        });
      } else {
        this.$root.$emit('selection-action', 'freehand', {
          selectionBoxAction: this.getIntersectingLinks,
          layerId: this.currentWaterDataProfileLayerId,
        });
      }
      this.isButtonActive = true;
    },
    getButtonListeners(on) {
      return this.isButtonActive
        ? {
            click: this.turnOffSelecting,
          }
        : on;
    },
    onElementHover(item, value) {
      this.onHover({ id: item.value, layerId: this.currentWaterDataProfileLayerId }, value);
    },
  },
  watch: {
    activeTool(nV, oV) {
      if (nV !== oV && oV === 'selection') {
        this.isButtonActive = false;
      }
    },
    currentWaterDataProfileLayerId(nV, oV) {
      this.clearSelection(oV);
    },
    waterDataProfileMappedLayers: {
      deep: true,
      immediate: true,
      handler(nV) {
        if (nV.find(layer => layer.value === this.currentWaterDataProfileLayerId)) {
          return;
        } else {
          this.currentWaterDataProfileLayerId = nV[0]?.value || null;
        }
      },
    },
  },
  beforeDestroy() {
    this.clearSelection();
  },
  mounted() {
    this.currentWaterDataProfileLayerId = this.waterDataProfileMappedLayers[0]?.value || null;
  },
};
</script>
